import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Minus, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Last 30 days of a routine with streak and adherence (PRD 6.4). */
export function RoutineTracker({ routineId }: { routineId: number }) {
  const { data } = useQuery({
    queryKey: ['occurrences', routineId],
    queryFn: () => api.occurrences(routineId),
  });
  const set = useMutation({
    mutationFn: ({ date, status }: { date: string; status: 'done' | 'skip' | 'open' }) =>
      api.setOccurrence(routineId, date, status),
  });
  if (!data) return null;
  const { stats } = data;
  return (
    <div className="space-y-3">
      <div className="flex gap-4 text-sm">
        <div>
          <div className="text-2xl font-semibold">{stats.streak}</div>
          <div className="text-xs text-muted-fg">day streak</div>
        </div>
        <div>
          <div className="text-2xl font-semibold">
            {stats.done} of {stats.due}
          </div>
          <div className="text-xs text-muted-fg">days done (last 30)</div>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {data.occurrences.map((o) => (
          <button
            key={o.date}
            title={`${o.date}: ${o.status}`}
            onClick={() => set.mutate({ date: o.date, status: o.status === 'done' ? 'open' : 'done' })}
            className={cn(
              'flex h-9 flex-col items-center justify-center rounded text-[10px]',
              o.status === 'done' && 'bg-ok/80 text-white',
              o.status === 'skipped' && 'bg-muted text-muted-fg line-through',
              o.status === 'open' && 'border border-border hover:bg-muted',
            )}
          >
            {o.date.slice(8)}
            {o.status === 'done' ? <Check size={10} /> : o.status === 'skipped' ? <Minus size={10} /> : null}
          </button>
        ))}
      </div>
      {data.occurrences.at(-1) && data.occurrences.at(-1)!.status === 'open' && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => set.mutate({ date: data.occurrences.at(-1)!.date, status: 'done' })}
          >
            <Check size={13} /> Done today
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => set.mutate({ date: data.occurrences.at(-1)!.date, status: 'skip' })}
          >
            <SkipForward size={13} /> Skip
          </Button>
        </div>
      )}
    </div>
  );
}
